import { api, qs } from '../lib/api';

/**
 * Everything the panel reads from /v1/admin, with the shapes the API sends.
 * Keep these in step with slate-api/src/routes/admin.ts.
 */

export type Role = 'user' | 'admin' | 'owner';
export type Seat = 'free' | 'paid';
export type SubscriptionStatus = 'incomplete' | 'trialing' | 'active' | 'past_due' | 'canceled';
export type VideoStatus = 'uploading' | 'processing' | 'ready' | 'failed';

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

export interface AdminMe {
  id: string;
  email: string;
  name: string | null;
}

export interface DailyPoint {
  day: string;
  count: number;
}

export interface Stats {
  users: { total: number; last7d: number; last30d: number };
  orgs: { total: number; byStatus: Partial<Record<SubscriptionStatus, number>> };
  videos: {
    total: number;
    last7d: number;
    byStatus: Partial<Record<VideoStatus, number>>;
    totalDurationSec: number;
    totalBytes: number | null;
  };
  featureRequests: { open: number };
  admins: { total: number };
  daily: { signups: DailyPoint[]; recordings: DailyPoint[] };
}

export interface OrgRef {
  id: string;
  name: string;
  slug: string;
}

export interface UserRef {
  id: string;
  email: string;
  name: string | null;
}

export interface UserRow extends UserRef {
  avatarUrl: string | null;
  createdAt: string;
  _count: { videos: number };
  memberships: Array<{ role: Role; seat: Seat; org: OrgRef }>;
}

export interface VideoRow {
  id: string;
  shareId: string;
  title: string;
  status: VideoStatus;
  kind: 'screen' | 'audio';
  workspace: 'personal' | 'team';
  visibility: 'public' | 'private';
  durationSec: number | null;
  sizeBytes: number | null;
  createdAt: string;
  readyAt: string | null;
  deletedAt: string | null;
  submitterName: string | null;
  owner: UserRef | null;
  org: OrgRef | null;
  _count: { views: number; comments: number };
}

export interface UserDetail extends UserRow {
  updatedAt: string;
  firebaseUid: string;
  _count: { videos: number; requestLinks: number; comments: number; deviceSessions: number };
  videos: VideoRow[];
}

export interface OrgRow extends OrgRef {
  subscriptionStatus: SubscriptionStatus;
  billingInterval: 'month' | 'year';
  seatCount: number;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  trialEndsAt: string | null;
  renewsAt: string | null;
  cancelAt: string | null;
  createdAt: string;
  memberCount: number;
  pendingInvites: number;
  seatsUsed: number;
  _count: { videos: number };
}

export interface OrgDetail extends OrgRef {
  subscriptionStatus: SubscriptionStatus;
  billingInterval: 'month' | 'year';
  seatCount: number;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  trialEndsAt: string | null;
  renewsAt: string | null;
  cancelAt: string | null;
  createdAt: string;
  updatedAt: string;
  memberships: Array<{
    id: string;
    role: Role;
    seat: Seat;
    status: 'active' | 'suspended';
    createdAt: string;
    user: UserRef & { avatarUrl: string | null };
  }>;
  invites: Array<{
    id: string;
    email: string;
    name: string | null;
    role: Role;
    seat: Seat;
    expiresAt: string;
    createdAt: string;
  }>;
  videos: VideoRow[];
  _count: { videos: number; requestLinks: number };
}

export interface FeatureRequest {
  id: string;
  title: string;
  body: string;
  contact: string;
  videoUrl: string | null;
  source: string;
  createdAt: string;
  handledAt: string | null;
}

export interface AdminRow {
  id: string;
  email: string;
  name: string | null;
  createdAt: string;
  lastSeenAt: string | null;
  signedIn: boolean;
  /** The "you are an admin" email: last successful send, last failure, how many went out. */
  invite: { sentAt: string | null; error: string | null; count: number };
  addedBy: UserRef | null;
}

/** One place for every React Query cache key, so invalidation is not guesswork. */
export const qk = {
  me: () => ['admin', 'me'] as const,
  stats: () => ['admin', 'stats'] as const,
  users: (q: string) => ['admin', 'users', q] as const,
  user: (id: string) => ['admin', 'user', id] as const,
  orgs: (q: string, status: string) => ['admin', 'orgs', q, status] as const,
  org: (id: string) => ['admin', 'org', id] as const,
  videos: (q: string, status: string, deleted: boolean) => ['admin', 'videos', q, status, deleted] as const,
  featureRequests: (handled: string) => ['admin', 'feature-requests', handled] as const,
  admins: () => ['admin', 'admins'] as const,
};

export const fetchMe = () => api<AdminMe>('/v1/admin/me');
export const fetchStats = () => api<Stats>('/v1/admin/stats');

export const fetchUsers = (q: string, cursor: string | null) =>
  api<Page<UserRow>>(`/v1/admin/users${qs({ q, cursor })}`);
export const fetchUser = (id: string) => api<UserDetail>(`/v1/admin/users/${id}`);

export const fetchOrgs = (q: string, status: string, cursor: string | null) =>
  api<Page<OrgRow>>(`/v1/admin/orgs${qs({ q, status, cursor })}`);
export const fetchOrg = (id: string) => api<OrgDetail>(`/v1/admin/orgs/${id}`);

export const fetchVideos = (q: string, status: string, deleted: boolean, cursor: string | null) =>
  api<Page<VideoRow>>(`/v1/admin/videos${qs({ q, status, deleted: deleted ? '1' : undefined, cursor })}`);

export const fetchFeatureRequests = (handled: string, cursor: string | null) =>
  api<Page<FeatureRequest>>(`/v1/admin/feature-requests${qs({ handled, cursor })}`);
export const setFeatureRequestHandled = (id: string, handled: boolean) =>
  api<FeatureRequest>(`/v1/admin/feature-requests/${id}`, { method: 'PATCH', body: { handled } });

export const fetchAdmins = () => api<{ items: AdminRow[] }>('/v1/admin/admins');
export const addAdmin = (body: { email: string; name: string | null }) =>
  api<AdminRow>('/v1/admin/admins', { method: 'POST', body });
export const removeAdmin = (id: string) => api<void>(`/v1/admin/admins/${id}`, { method: 'DELETE' });
export const resendAdminInvite = (id: string) => api<AdminRow>(`/v1/admin/admins/${id}/invite`, { method: 'POST' });
